import { FC, useState } from "react";
import { Calendar, ChevronDown } from "lucide-react";
import {
	buildPeriod,
	formatRange,
	fromDateInputValue,
	periodFromQuickRange,
	toDateInputValue,
	type Period,
	type QuickRange
} from "../../utils/period";

interface Props {
	value: Period;
	onChange: (next: Period) => void;
}

const QUICK_RANGES: { key: QuickRange; label: string }[] = [
	{ key: "today", label: "Today" },
	{ key: "7d", label: "7d" },
	{ key: "30d", label: "30d" },
	{ key: "90d", label: "90d" },
	{ key: "12m", label: "12m" }
];

const dayMs = 24 * 60 * 60 * 1000;

// Periods use a half-open `[from, to)` range where `to` is next-day
// UTC midnight (exclusive). The custom date inputs show users the
// inclusive end-date — without this conversion, opening Custom on a
// quick range and pressing Apply unchanged would silently shift the
// window forward by one day each time, because handleCustom adds a
// day on apply.
const toInclusiveDateInputValue = (to: Date): string => {
	const isUtcMidnight =
		to.getUTCHours() === 0 &&
		to.getUTCMinutes() === 0 &&
		to.getUTCSeconds() === 0 &&
		to.getUTCMilliseconds() === 0;
	const display = isUtcMidnight ? new Date(to.getTime() - dayMs) : to;
	return toDateInputValue(display);
};

const PeriodPicker: FC<Props> = ({ value, onChange }) => {
	const [customOpen, setCustomOpen] = useState(false);
	const [customFrom, setCustomFrom] = useState(toDateInputValue(value.from));
	const [customTo, setCustomTo] = useState(toInclusiveDateInputValue(value.to));
	const isCustom = value.kind === "custom";
	const drawerId = "period-picker-custom-drawer";

	const apply = (next: Period) => {
		setCustomFrom(toDateInputValue(next.from));
		setCustomTo(toInclusiveDateInputValue(next.to));
		onChange(next);
	};

	const handleQuick = (key: QuickRange) => {
		apply(periodFromQuickRange(key));
		setCustomOpen(false);
	};

	const handleCustom = () => {
		const from = fromDateInputValue(customFrom);
		const inclusiveTo = fromDateInputValue(customTo);
		if (!from || !inclusiveTo) return;
		// API contract is half-open `[from, to)` with `to` at next-day
		// UTC midnight. The picker shows an inclusive end-date, so add
		// one day on apply. Without this, picking "April 1 → April 30"
		// would silently drop April 30 from every chart.
		const to = new Date(inclusiveTo.getTime() + dayMs);
		if (from >= to) return;
		apply(buildPeriod(from, to, "Custom", "custom"));
		setCustomOpen(false);
	};

	return (
		<div
			role="group"
			aria-label="Time period"
			className="w-full bg-white border border-border rounded-2xl shadow-xs p-2"
		>
			<div className="flex items-center gap-3 flex-wrap">
				<div className="flex flex-wrap items-center gap-1">
					{QUICK_RANGES.map(({ key, label }) => {
						const isSelected =
							!isCustom &&
							value.label.toLowerCase() ===
								periodFromQuickRange(key).label.toLowerCase();
						return (
							<button
								key={key}
								type="button"
								aria-pressed={isSelected}
								onClick={() => handleQuick(key)}
								className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
									isSelected
										? "bg-primary text-white shadow-sm"
										: "text-zinc-600 hover:text-foreground hover:bg-subtle"
								}`}
							>
								{label}
							</button>
						);
					})}
					<span className="w-px h-5 bg-border mx-1" aria-hidden="true" />
					<button
						type="button"
						aria-pressed={isCustom}
						aria-expanded={customOpen}
						aria-controls={drawerId}
						onClick={() => setCustomOpen((v) => !v)}
						className={`px-3.5 py-1.5 text-xs font-medium rounded-full inline-flex items-center gap-1.5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
							isCustom
								? "bg-primary text-white shadow-sm"
								: "text-zinc-600 hover:text-foreground hover:bg-subtle"
						}`}
					>
						<Calendar className="h-3.5 w-3.5" />
						Custom
						<ChevronDown
							className={`h-3 w-3 transition-transform ${
								customOpen ? "rotate-180" : ""
							}`}
						/>
					</button>
				</div>
				<p className="ml-auto text-xs text-muted-foreground pr-2">
					<span className="font-medium text-foreground">{value.label}</span>
					<span className="mx-1.5">·</span>
					<span role="status">{formatRange(value.from, value.to)}</span>
				</p>
			</div>
			{customOpen ? (
				<div
					id={drawerId}
					className="mt-2 flex items-center gap-3 flex-wrap border-t border-border pt-3 px-2"
				>
					{/* Wrapping <label> already provides the accessible name
					    via the visible "From" / "To" text — adding an
					    `aria-label` would override it and break WCAG 2.5.3
					    Label in Name. */}
					<label className="text-xs text-muted-foreground">
						From
						<input
							type="date"
							className="ml-2 border border-border rounded-md px-2 py-1 text-xs"
							value={customFrom}
							onChange={(e) => setCustomFrom(e.target.value)}
						/>
					</label>
					<label className="text-xs text-muted-foreground">
						To
						<input
							type="date"
							className="ml-2 border border-border rounded-md px-2 py-1 text-xs"
							value={customTo}
							onChange={(e) => setCustomTo(e.target.value)}
						/>
					</label>
					<button
						type="button"
						onClick={handleCustom}
						className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-white hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
					>
						Apply
					</button>
				</div>
			) : null}
		</div>
	);
};

export default PeriodPicker;
